// Sound for the TV board: one AudioContext for the whole page plus the speech engine.
// Browsers refuse audio until the page has had a user gesture, so `unlock()` must run inside a click, touchend or
// keydown handler; `state` tells the screen what to show. Timing, the call-out text and the voice rule come from
// packages/core (board.ts, spoken.ts): a number is said as a whole number in each board language that has a voice,
// and a language without one is skipped, so the chime alone is the alert rather than a foreign voice reading it.
import { BOARD_CHIME, boardCallOut, chimeDurationMs, chimeSchedule, pickVoice, voiceLangsAvailable, type Lang } from '@ringring/core';

export type SoundState = 'locked' | 'on' | 'unavailable';

type AudioContextCtor = typeof AudioContext;

export class BoardSound {
  private ctx: AudioContext | null = null;
  private readonly listeners = new Set<(s: SoundState) => void>();
  private voices: SpeechSynthesisVoice[] = [];

  constructor() {
    try {
      const A: AudioContextCtor | undefined = window.AudioContext ?? (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext;
      if (A) {
        this.ctx = new A();
        this.ctx.onstatechange = () => this.emit();
      }
    } catch {
      this.ctx = null;
    }
    // Chrome fills the voice list after load and says so with voiceschanged; an engine may fire it again when a
    // language pack is installed. The list is re-read before every call-out anyway; the event only refreshes the screen.
    try {
      const s = this.speech();
      if (s) {
        this.refreshVoices();
        s.onvoiceschanged = () => {
          this.refreshVoices();
          this.emit();
        };
      }
    } catch {
      // no speech engine
    }
  }

  get state(): SoundState {
    if (!this.ctx) return 'unavailable';
    return this.ctx.state === 'running' ? 'on' : 'locked';
  }

  subscribe(fn: (s: SoundState) => void): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  private emit(): void {
    for (const fn of this.listeners) fn(this.state);
  }

  private speech(): SpeechSynthesis | null {
    try {
      return window.speechSynthesis ?? null;
    } catch {
      return null;
    }
  }

  private refreshVoices(): SpeechSynthesisVoice[] {
    try {
      this.voices = this.speech()?.getVoices() ?? [];
    } catch {
      this.voices = [];
    }
    return this.voices;
  }

  /** Which of these languages this browser can speak right now, in order. Empty means the chime is the whole alert. */
  voiceLangs(langs: readonly Lang[]): Lang[] {
    return voiceLangsAvailable(this.refreshVoices(), langs);
  }

  /** Call inside a user gesture: plays a one-sample silent buffer and resumes the context (what iOS and some TV browsers need), then primes speech. */
  unlock(): void {
    const ctx = this.ctx;
    if (ctx) {
      try {
        const src = ctx.createBufferSource();
        src.buffer = ctx.createBuffer(1, 1, 22050);
        src.connect(ctx.destination);
        src.start(0);
      } catch {
        // the silent sample is a nicety; resume() below is what matters
      }
      this.resume();
    }
    try {
      const s = this.speech();
      if (s) {
        this.refreshVoices();
        s.cancel();
        s.speak(new SpeechSynthesisUtterance(' '));
      }
    } catch {
      // no speech engine
    }
    this.emit();
  }

  /** Try to resume without a gesture: the page shown again, or a kiosk browser that allows autoplay. */
  resume(): void {
    try {
      void this.ctx?.resume().then(
        () => this.emit(),
        () => this.emit(),
      );
    } catch {
      // stays locked until a gesture
    }
  }

  /** Two rising notes; returns false when the browser still refuses audio. */
  chime(): boolean {
    const ctx = this.ctx;
    if (!ctx || ctx.state !== 'running') return false;
    try {
      const t0 = ctx.currentTime;
      for (const n of chimeSchedule()) {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.frequency.value = n.frequencyHz;
        o.connect(g);
        g.connect(ctx.destination);
        g.gain.setValueAtTime(0.0001, t0 + n.start);
        g.gain.exponentialRampToValueAtTime(BOARD_CHIME.peakGain, t0 + n.start + 0.03);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + n.stop);
        o.start(t0 + n.start);
        o.stop(t0 + n.stop + 0.05);
      }
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Say each number as a whole number, in each of `langs` that has a voice (the venue default first, then the
   * optional second language), once the chime has finished. Returns the languages that will be spoken: empty when
   * sound is locked or no voice exists, in which case the chime is the whole alert. Speech follows the same gesture rule as audio.
   */
  callOut(numbers: readonly string[], langs: readonly Lang[]): Lang[] {
    if (this.state !== 'on' || numbers.length === 0) return [];
    const engine = this.speech();
    if (!engine) return [];
    const voices = this.refreshVoices();
    const plan: { lang: Lang; voice: SpeechSynthesisVoice }[] = [];
    for (const lang of langs) {
      const voice = pickVoice(voices, lang);
      if (voice) plan.push({ lang, voice });
    }
    if (plan.length === 0) return [];
    window.setTimeout(() => {
      try {
        for (const no of numbers) {
          for (const { lang, voice } of plan) {
            const u = new SpeechSynthesisUtterance(boardCallOut(no, lang));
            u.voice = voice;
            u.lang = voice.lang;
            u.rate = 0.9;
            engine.speak(u);
          }
        }
      } catch {
        // no speech engine
      }
    }, chimeDurationMs());
    return plan.map((p) => p.lang);
  }
}

let shared: BoardSound | null = null;

/** The page's single sound engine; created on first use so StrictMode's double render does not open two contexts. */
export function boardSound(): BoardSound {
  if (!shared) shared = new BoardSound();
  return shared;
}
